import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import '../../assets/styles/ClientShared.css';

const questions = [
  "Do all pages load correctly and quickly on both desktop and mobile?",
  "Are there any broken links or buttons?",
  "Are all forms (contact, sign-up, etc.) working correctly?",
  "Is the content free of typos, grammar issues, or outdated info?",
  "Does the design match your expectations on all devices?",
  "Are all animations, transitions, and hover states functioning smoothly?",
  "Does the website work across different browsers (Chrome, Safari, Firefox)?",
  "Are third-party tools (Calendly, payment gateways, etc.) integrated and functional?",
  "Is the favicon and metadata correctly set (title, description)?",
  "Do you have any final requests or changes before we launch?"
];

const TestingQA = () => {
  const [previewLink, setPreviewLink] = useState("");
  const [responses, setResponses] = useState({});
  const [approval, setApproval] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User not found.");

        const userData = userSnap.data();
        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);
        if (!projectSnap.exists()) throw new Error("Project not found.");

        const projectData = projectSnap.data();
        const qaData = projectData.revisionDetails?.testingQA || {};
        setPreviewLink(qaData.link || "");
        setResponses(qaData.form?.responses || {});
        setApproval(qaData.form?.approval || false);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (question, value) => {
    setResponses((prev) => ({ ...prev, [question]: value }));
  };

  const handleSubmit = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated.");
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const projectRef = doc(db, "projects", userData.projectId);

      await updateDoc(projectRef, {
        "revisionDetails.testingQA.form": {
          responses,
          approval,
        },
      });

      toast.success("Testing & QA feedback saved successfully.");
    } catch (err) {
      toast.error("Failed to save: " + err.message);
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading...</p></div>;
  }

  if (error) {
    return <p className="cp-message--error">{error}</p>;
  }

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Testing & QA</h1>
        <p className="cp-subtitle">Final review before launch. Check each item and provide feedback.</p>
      </div>

      {/* Preview link */}
      {previewLink && (
        <div className="cp-section" style={{marginBottom:'20px'}}>
          <div className="cp-section-header"><h2 className="cp-section-title">Preview Link</h2></div>
          <div className="cp-section-body">
            <a href={previewLink.startsWith('http')?previewLink:`https://${previewLink}`} target="_blank" rel="noopener noreferrer" className="cp-doc-link" style={{fontSize:'0.9375rem'}}>View Website Preview →</a>
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="cp-section" style={{marginBottom:'20px'}}>
        <div className="cp-section-header"><h2 className="cp-section-title">QA Checklist</h2></div>
        <div className="cp-section-body">
          <ul className="cp-checklist">
            {questions.map((question, index) => (
              <li key={index} className="cp-checklist-item">
                <input type="checkbox" checked={responses[question]||false} onChange={e=>handleInputChange(question, e.target.checked)}/>
                <span>{question}</span>
              </li>
            ))}
            <li className="cp-checklist-item" style={{paddingTop:'14px',borderTop:'1px solid var(--border)',marginTop:'6px'}}>
              <input type="checkbox" checked={approval} onChange={e=>setApproval(e.target.checked)}/>
              <span style={{fontWeight:600}}>I have tested the website and approve it for launch.</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="cp-btn-row">
        <button className="cp-btn-primary" onClick={handleSubmit}>Save Feedback</button>
      </div>
    </div>
  );
};

export default TestingQA;
